import { downloadTextFile } from "./downloadFile";

export type CalendarDateInput = string | Date;

export interface CalendarEventExport {
  uid: string;
  title: string;
  startsAt: CalendarDateInput;
  endsAt?: CalendarDateInput | null;
  /** "YYYY-MM-DD": an all-day event on that day; startsAt and endsAt then give no times. */
  allDayDate?: string | null;
  description?: string | null;
  location?: string | null;
  url?: string | null;
}

export interface IcsCalendarOptions {
  productId: string;
  timestamp?: CalendarDateInput;
  method?: string;
}

export interface GoogleCalendarOptions {
  timeZone?: string;
}

const toValidDate = (value: CalendarDateInput, fieldName: string): Date => {
  const date = value instanceof Date ? new Date(value.getTime()) : new Date(value);
  if (Number.isNaN(date.getTime())) {
    throw new Error(`Invalid calendar date for ${fieldName}`);
  }
  return date;
};

const requireText = (value: string, fieldName: string): string => {
  if (!value.trim()) {
    throw new Error(`Calendar ${fieldName} is required`);
  }
  return value;
};

const hasText = (value: string | null | undefined): value is string =>
  typeof value === "string" && value.trim().length > 0;

export function escapeIcsText(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/\r\n|\r|\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");
}

export function formatUtcCalendarDate(value: CalendarDateInput): string {
  return toValidDate(value, "value")
    .toISOString()
    .replace(/[-:]/g, "")
    .replace(/\.\d{3}Z$/, "Z");
}

const compactDate = (value: string) => value.replace(/-/g, "");

/** The day after a "YYYY-MM-DD" date, as "YYYYMMDD": the exclusive end of an all-day event. */
const nextCompactDate = (value: string) => {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day + 1)).toISOString().slice(0, 10).replace(/-/g, "");
};

export function serializeIcsEvent(
  event: CalendarEventExport,
  options: IcsCalendarOptions,
): string {
  const uid = requireText(event.uid, "event UID");
  const title = requireText(event.title, "event title");
  const productId = requireText(options.productId, "product ID");
  const timestamp = formatUtcCalendarDate(options.timestamp ?? new Date());
  const startsAt = formatUtcCalendarDate(event.startsAt);
  const endsAt = event.endsAt == null ? null : formatUtcCalendarDate(event.endsAt);

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    `PRODID:${productId}`,
    "CALSCALE:GREGORIAN",
  ];

  if (hasText(options.method)) lines.push(`METHOD:${options.method}`);
  lines.push(
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${timestamp}`,
  );
  if (event.allDayDate) {
    lines.push(`DTSTART;VALUE=DATE:${compactDate(event.allDayDate)}`, `DTEND;VALUE=DATE:${nextCompactDate(event.allDayDate)}`);
  } else {
    lines.push(`DTSTART:${startsAt}`);
    if (endsAt) lines.push(`DTEND:${endsAt}`);
  }
  lines.push(`SUMMARY:${escapeIcsText(title)}`);
  if (hasText(event.description)) lines.push(`DESCRIPTION:${escapeIcsText(event.description)}`);
  if (hasText(event.location)) lines.push(`LOCATION:${escapeIcsText(event.location)}`);
  if (hasText(event.url)) lines.push(`URL:${event.url}`);
  lines.push("END:VEVENT", "END:VCALENDAR");

  return `${lines.join("\r\n")}\r\n`;
}

export function buildGoogleCalendarUrl(
  event: CalendarEventExport,
  options: GoogleCalendarOptions = {},
): string {
  const title = requireText(event.title, "event title");
  const startsAt = formatUtcCalendarDate(event.startsAt);
  const endsAt = event.endsAt == null ? startsAt : formatUtcCalendarDate(event.endsAt);
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: title,
    dates: event.allDayDate ? `${compactDate(event.allDayDate)}/${nextCompactDate(event.allDayDate)}` : `${startsAt}/${endsAt}`,
  });
  const details = [event.description, event.url].filter(hasText).join("\n\n");

  if (details) params.set("details", details);
  if (hasText(event.location)) params.set("location", event.location);
  if (hasText(options.timeZone)) params.set("ctz", options.timeZone);

  const url = new URL("https://calendar.google.com/calendar/render");
  url.search = params.toString();
  return url.toString();
}

export function downloadIcsEvent(
  event: CalendarEventExport,
  filename: string,
  options: IcsCalendarOptions,
): void {
  downloadTextFile(
    serializeIcsEvent(event, options),
    filename,
    "text/calendar;charset=utf-8",
  );
}
